<script setup lang="ts">
import {Badge} from '@hantera/design-system'
import {useLocalization} from '@hantera/portal-app'
import type {Session} from '@/backend/iam.types'

const t = useLocalization('iam.principals')

defineProps<{
  sessions: Session[]
  canRevoke: boolean
}>()

const emit = defineEmits<{
  revoke: [sessionId: string]
}>()

function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return t('time.justNow')
  if (diffMins < 60) {
    const plural = diffMins > 1 ? 's' : ''
    return t('time.minutesAgo', { count: diffMins, plural })
  }
  if (diffHours < 24) {
    const plural = diffHours > 1 ? 's' : ''
    return t('time.hoursAgo', { count: diffHours, plural })
  }
  if (diffDays < 7) {
    const plural = diffDays > 1 ? 's' : ''
    return t('time.daysAgo', { count: diffDays, plural })
  }

  return formatDateTime(date)
}
</script>

<template>
  <div class="session-list">
    <div v-if="sessions.length === 0" class="empty-state">
      <p>{{ t('sessions.noActiveSessions') }}</p>
    </div>
    
    <div v-for="session in sessions" :key="session.id" class="session-item box" :class="{ 'session-revoked': session.isRevoked }">
      <div class="session-info">
        <div class="session-type">
          <Badge variant="primary" :uppercase="true">{{ session.type }}</Badge>
          <Badge v-if="session.isCurrent" variant="success">{{ t('sessions.current') }}</Badge>
          <Badge v-if="session.isRevoked" variant="neutral">{{ t('sessions.revoked') }}</Badge>
        </div>
        <div v-if="session.description" class="session-description">
          {{ session.description }}
        </div>
        <div class="session-created">
          {{ t('sessions.created') }}: {{ formatRelativeTime(session.createdAt) }}
        </div>
        <div class="session-expires">
          {{ t('sessions.expires') }}: {{ formatDateTime(session.accessTokenExpiresAt) }}
        </div>
        <div v-if="session.clientId" class="session-client">
          {{ t('sessions.client') }}: {{ session.clientName || session.clientId }}
        </div>
        <div v-if="session.isRevoked && session.revokedAt" class="session-revoked-at">
          {{ t('sessions.revokedAt') }}: {{ formatRelativeTime(session.revokedAt) }}
        </div>
      </div>
      <button
        v-if="canRevoke && !session.isCurrent && !session.isRevoked"
        @click="emit('revoke', session.id)"
        class="btn-danger"
        :title="t('sessions.revokeButtonTitle')"
      >
        {{ t('sessions.revoke') }}
      </button>
      <span v-else-if="session.isCurrent" class="current-note">
        {{ t('sessions.currentSession') }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.session-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.session-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.session-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.session-type {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.session-revoked-at {
  font-size: 0.875rem;
  color: #999;
  font-style: italic;
}

.session-created,
.session-expires,
.session-client {
  font-size: 0.875rem;
  color: #666;
}

.current-note {
  font-size: 0.875rem;
  color: #999;
  font-style: italic;
}
</style>
